import { Range, getTrackBackground } from 'react-range';
import cx from 'classnames';
import React, { Dispatch, SetStateAction, useState } from 'react';
import classNames from 'classnames';

const isNumeric = (n: any) => !isNaN(parseFloat(n)) && isFinite(n);

const MAX_SAVINGS_LIMIT = 100000;
const MAX_CONTRIBUTIONS_LIMIT = 10000;

interface StateHandlersType {
  setCurrentSavings: Dispatch<SetStateAction<string>>;
  setRateOfReturn: Dispatch<SetStateAction<number | 'custom'>>;
  setCustomRate: Dispatch<SetStateAction<string>>;
  setExtraContribution: Dispatch<SetStateAction<string>>;
  setContributionFrequency: Dispatch<SetStateAction<number>>;
  setSavingsAgeRange: Dispatch<SetStateAction<[number, number]>>;
}

interface SavingsCalculatorFormData {
  currentSavings: string;
  rateOfReturn: number | 'custom';
  customRate: string;
  extraContribution: string;
  contributionFrequency: number;
  savingsAgeRange: [number, number];
  estimatedSavings: number;
}

export interface SavingsCalculatorFormProps {
  stateHandlers: StateHandlersType;
  formData: SavingsCalculatorFormData;
  formSectionHeader?: string;
  currentSavingsHeader?: string;
  contributionsHeader?: string;
  frequencyHeader?: string;
  frequencyOptions?: (
    | 'Bi-weekly'
    | 'Daily'
    | 'Monthly'
    | 'Quarterly'
    | 'Weekly'
    | 'Yearly'
  )[];
  savingsRangeHeader?: string;
  savingsRateHeader?: string;
  savingsRateBody?: string;
  totalSavingsHeader?: string;
}

export const SavingsForm = ({
  stateHandlers,
  formData,
  formSectionHeader,
  currentSavingsHeader,
  contributionsHeader,
  frequencyHeader,
  frequencyOptions,
  savingsRangeHeader,
  savingsRateHeader,
  savingsRateBody,
  totalSavingsHeader
}: SavingsCalculatorFormProps) => {
  const {
    currentSavings,
    rateOfReturn,
    extraContribution,
    contributionFrequency,
    savingsAgeRange
  } = formData;

  const {
    setCurrentSavings,
    setRateOfReturn,
    setExtraContribution,
    setContributionFrequency,
    setSavingsAgeRange
  } = stateHandlers;

  const handleMonetaryValueChange = (
    value: string,
    func: Dispatch<SetStateAction<string>>
  ) => {
    if (isNumeric(value) || value === '') {
      func(value === '' ? '0' : value);
    }
  };

  // Add touched state for both inputs
  const [currentSavingsTouched, setCurrentSavingsTouched] = useState(false);
  const [extraContributionTouched, setExtraContributionTouched] =
    useState(false);

  return (
    <div className="flex w-full flex-col gap-6 tablet:rounded-card-shape tablet:bg-white tablet:p-6 desktop:w-1/3">
      {/* Form Fields */}
      {formSectionHeader && (
        <h2 className="typography-display-4 hidden font-normal tablet:block">
          {formSectionHeader}
        </h2>
      )}
      {/* Initial Deposit */}
      <div className="flex flex-col gap-2">
        {currentSavingsHeader && (
          <label
            htmlFor="currentSavings"
            className="typography-title-5 font-medium"
          >
            {currentSavingsHeader}
          </label>
        )}
        <div className="relative">
          <input
            id={'currentSavings'}
            data-testid="current-savings"
            type="text"
            inputMode="numeric"
            aria-label={currentSavingsHeader || 'Current savings amount'}
            aria-describedby="current-savings-description"
            value={
              currentSavings
                ? new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 0
                  }).format(parseInt(currentSavings))
                : ''
            }
            onFocus={() => setCurrentSavingsTouched(true)}
            onChange={(e) => {
              if (!currentSavingsTouched) setCurrentSavingsTouched(true);
              const value = e.target.value.replace(/[^\d]/g, '');
              const numericValue = parseFloat(value.replace(/,/g, ''));

              if (numericValue <= MAX_SAVINGS_LIMIT || isNaN(numericValue)) {
                handleMonetaryValueChange(value, setCurrentSavings);
              }
            }}
            className={cx(
              'typography-title-4 flex h-10 w-full items-center justify-center gap-2 rounded-input-box-shape  border border-solid border-[#3436361a] px-1 text-center font-medium focus:outline-[#00D9B4]',
              !currentSavingsTouched && 'text-[#676c6f] text-opacity-50'
            )}
          />
          <span id="current-savings-description" className="sr-only">
            Enter your current savings amount in dollars. Maximum value is
            $100,000.
          </span>
        </div>
      </div>
      {/* Regular Contributions */}
      <div className="flex flex-col gap-2">
        {contributionsHeader && (
          <label
            htmlFor="extraContribution"
            className="typography-title-5 font-medium"
          >
            {contributionsHeader}
          </label>
        )}
        <div className="relative">
          <input
            id="extraContribution"
            type="text"
            inputMode="numeric"
            max={10000}
            aria-label={contributionsHeader || 'Regular contribution amount'}
            aria-describedby="extra-contribution-description"
            value={
              extraContribution
                ? new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 0
                  }).format(parseInt(extraContribution))
                : ''
            }
            onFocus={() => setExtraContributionTouched(true)}
            onChange={(e) => {
              if (!extraContributionTouched) setExtraContributionTouched(true);
              const value = e.target.value.replace(/[^\d]/g, '');
              const numericValue = parseFloat(value.replace(/,/g, ''));

              if (
                numericValue <= MAX_CONTRIBUTIONS_LIMIT ||
                isNaN(numericValue)
              ) {
                handleMonetaryValueChange(value, setExtraContribution);
              }
            }}
            className={cx(
              'typography-title-4 flex h-10 w-full items-center justify-center gap-2 rounded-input-box-shape  border border-solid border-[#3436361a] px-1 text-center font-medium focus:outline-[#00D9B4]',
              !extraContributionTouched && 'text-[#676c6f] text-opacity-50'
            )}
          />
          <span id="extra-contribution-description" className="sr-only">
            Enter your regular contribution amount in dollars. Maximum value is
            $10,000.
          </span>
        </div>
      </div>
      {/* Contribution Frequency */}
      <div className="flex flex-col gap-2">
        {frequencyHeader && (
          <label
            id="contribution-frequency-label"
            className="typography-title-5 font-medium"
          >
            {frequencyHeader}
          </label>
        )}
        {frequencyOptions && (
          <div
            role="radiogroup"
            aria-labelledby="contribution-frequency-label"
            className="flex gap-2"
          >
            {frequencyOptions.map((label) => {
              let freq = 12;
              switch (label) {
                case 'Bi-weekly':
                  freq = 26;
                  break;
                case 'Daily':
                  freq = 365;
                  break;
                case 'Monthly':
                  break;
                case 'Quarterly':
                  freq = 4;
                  break;
                case 'Weekly':
                  freq = 52;
                  break;
                case 'Yearly':
                  freq = 1;
                  break;
              }
              const isSelected = contributionFrequency === freq;
              return (
                <button
                  key={label}
                  role="radio"
                  aria-checked={isSelected}
                  aria-label={`${label} contribution frequency`}
                  className={cx(
                    'typography-title-4 flex h-10 flex-grow  items-center justify-center gap-2 rounded-button-shape border-[1.5px] bg-white px-1 font-medium hover:border-teal',
                    isSelected ? '!border-secondary-dark' : 'border-[#3436361a]'
                  )}
                  onClick={() => setContributionFrequency(freq)}
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault();
                      setContributionFrequency(freq);
                    }
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {/* Savings Age Range */}
      <div className="flex flex-col gap-3">
        {savingsRangeHeader && (
          <label id="savings-age-range-label" className="">
            <span className="typography-title-5 font-medium">
              {savingsRangeHeader}:{' '}
            </span>
            <span className="typography-body-2 font-normal">
              {Math.max(0, savingsAgeRange[1] - savingsAgeRange[0])} years
            </span>
          </label>
        )}
        <div
          role="group"
          aria-labelledby="savings-age-range-label"
          aria-label={`Age range slider: ${savingsAgeRange[0]} to ${savingsAgeRange[1]} years`}
        >
          <Range
            values={savingsAgeRange}
            step={1}
            min={1}
            max={18}
            onChange={(values) =>
              setSavingsAgeRange(values as [number, number])
            }
            renderTrack={({ props, children }) => (
              <div
                {...props}
                className="mb-[14px] mt-[54px] h-1 rounded-lg bg-blue pr-2"
                style={{
                  background: getTrackBackground({
                    values: savingsAgeRange,
                    colors: ['#DFF6F0', '#0A7266', '#DFF6F0'],
                    min: 1,
                    max: 18
                  }),
                  position: 'relative',
                  marginInline: 'auto',
                  width: 'calc(100% - 16px)'
                }}
              >
                {children}
              </div>
            )}
            renderThumb={({ index, props, isDragged }) => {
              const value = savingsAgeRange[index];
              const isMin = index === 0;
              return (
                <div
                  {...props}
                  role="slider"
                  aria-label={isMin ? 'Minimum age' : 'Maximum age'}
                  aria-valuemin={1}
                  aria-valuemax={18}
                  aria-valuenow={value}
                  aria-valuetext={`${value} years old`}
                  tabIndex={0}
                  onKeyDown={(e: React.KeyboardEvent) => {
                    const newValues = [...savingsAgeRange] as [number, number];
                    let newValue = value;

                    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
                      e.preventDefault();
                      newValue = Math.max(1, value - 1);
                    } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
                      e.preventDefault();
                      newValue = Math.min(18, value + 1);
                    } else if (e.key === 'Home') {
                      e.preventDefault();
                      newValue = 1;
                    } else if (e.key === 'End') {
                      e.preventDefault();
                      newValue = 18;
                    }

                    if (newValue !== value) {
                      newValues[index] = newValue;
                      // Ensure min doesn't exceed max and vice versa
                      if (index === 0 && newValues[0] >= newValues[1]) {
                        newValues[1] = Math.min(18, newValues[0] + 1);
                      } else if (index === 1 && newValues[1] <= newValues[0]) {
                        newValues[0] = Math.max(1, newValues[1] - 1);
                      }
                      setSavingsAgeRange(newValues);
                    }
                  }}
                  className="relative -mt-[1.15rem] !h-15 !w-16"
                >
                  <div
                    className={classNames(
                      'absolute bottom-0 left-5 flex h-6 w-6 items-center justify-center rounded-full p-1 shadow-lg transition-colors duration-300',
                      { 'bg-teal-800': !isDragged, 'bg-primary': isDragged }
                    )}
                  />
                  <div className="typography-title-5 absolute bottom-7 left-1/2 flex -translate-x-1/2 transform text-nowrap rounded bg-teal-800 px-2 py-1 text-center font-medium text-white">
                    {value} y/o
                  </div>
                </div>
              );
            }}
          />
        </div>
      </div>
      {/* Savings Rate */}
      <div>
        {savingsRateHeader && (
          <p
            id="savings-rate-label"
            className="typography-title-5 pb-2 font-medium"
          >
            {savingsRateHeader}
          </p>
        )}
        {savingsRateBody && (
          <p className="typography-body-2 mb-4 font-light">{savingsRateBody}</p>
        )}
        <div
          role="radiogroup"
          aria-labelledby="savings-rate-label"
          className="grid grid-cols-4 gap-2"
        >
          {[
            { percent: 0.06, label: 'Family Shield' },
            { percent: 0.05, label: 'Infinity' },
            { percent: 0.03, label: 'Max' },
            { percent: 0.02, label: 'Core' }
          ].map((option) => {
            function toPercent(percent: number): import('react').ReactNode {
              return (
                <span className="typography-title-3 font-medium">
                  {(percent * 100).toFixed(0)}%
                </span>
              );
            }
            const isSelected = rateOfReturn === option.percent;
            const percentText = `${(option.percent * 100).toFixed(0)}%`;
            return (
              <button
                key={option.label}
                role="radio"
                aria-checked={isSelected}
                aria-label={`${option.label} savings rate, ${percentText}`}
                className={cx(
                  'flex flex-grow flex-col items-center justify-center gap-[2px] rounded-button-shape border-[1.5px] bg-white p-2 hover:border-teal',
                  isSelected ? '!border-secondary-dark' : 'border-[#3436361a]'
                )}
                onClick={() => setRateOfReturn(option.percent as any)}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    setRateOfReturn(option.percent as any);
                  }
                }}
              >
                {toPercent(option.percent)}
                <span className="typography-body-3 font-light">
                  {option.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      {/* Estimated Savings for Mobile */}
      <div className="desktop:hidden">
        <div className="flex flex-col items-center justify-center gap-2 rounded-card-inside-card-shape bg-brand-weakest py-3 desktop:hidden">
          {totalSavingsHeader && (
            <p className="typography-title-5 font-medium">
              {totalSavingsHeader}
            </p>
          )}
          <h2 className="text-[56px] font-normal leading-[64px]">
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(formData.estimatedSavings)}
          </h2>
        </div>
      </div>
    </div>
  );
};
